import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Phone, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";

export default function Contacto() {
  useSEO({
    title: "Contacto - Hablemos de tu proyecto | Globaltis IA",
    description: "Contacta con Globaltis para implementar IA en tu negocio. Respuesta en menos de 24 horas. Sin compromiso.",
  });

  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const tipo = searchParams.get("tipo");
  const capsula = searchParams.get("capsula");
  
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    empresa: "",
    mensaje: "",
    intentoDIY: "no",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (capsula) {
      setFormData(prev => ({
        ...prev,
        mensaje: `Me gustaría implementar la solución de ${capsula.replace(/-/g, " ")} en mi negocio.`,
      }));
    }
  }, [capsula]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await supabase.from("contact_submissions" as any).insert({
        nombre: formData.nombre,
        email: formData.email,
        empresa: formData.empresa || null,
        mensaje: formData.mensaje,
        source: "contacto",
      });
      
      toast({
        title: "¡Solicitud enviada!",
        description: "Te contactaremos en las próximas 24 horas.",
      });
      
      setFormData({
        nombre: "",
        email: "",
        empresa: "",
        mensaje: "",
        intentoDIY: "no",
      });
    } catch (err) {
      console.error("Error saving contact:", err);
      toast({
        title: "Error",
        description: "No se pudo enviar. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Main Contact Section - Steve Jobs Style */}
      <section className="min-h-screen py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* Left Column - Presentation */}
          <div className="space-y-8 animate-fade-in">
            <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
              Contacto
            </p>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light leading-[1.1] tracking-tight text-foreground">
              ¿En qué podemos<br />ayudarte?
            </h1>
            
            <div className="space-y-4">
              <p className="text-lg sm:text-2xl md:text-3xl font-light text-muted-foreground">
                Completa el formulario.
              </p>
              <p className="text-xl font-light text-muted-foreground">
                Te respondemos en menos de 24 horas.
              </p>
            </div>

            {/* Three contact categories */}
            <div className="max-w-4xl space-y-8 pt-12">
              <p className="text-lg text-muted-foreground font-light">
                ¿Qué necesitas?
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {/* Card 1: Preguntas */}
                <div className="p-5 sm:p-8 rounded-3xl border border-border/30 hover:border-primary/50 transition-all">
                  <div className="text-5xl mb-4">💬</div>
                  <h3 className="text-xl font-semibold mb-2">Tengo una pregunta</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Sobre herramientas, guías o cómo funciona algo
                  </p>
                  <p className="text-xs text-green-600">⏱️ Respuesta en 24h</p>
                </div>

                {/* Card 2: Trabajos específicos */}
                <div className="p-5 sm:p-8 rounded-3xl border-2 border-primary/50 bg-primary/5">
                  <div className="text-5xl mb-4">⚡</div>
                  <h3 className="text-xl font-semibold mb-2">Trabajo específico</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Video, presentación, propuesta o web con IA
                  </p>
                  <p className="text-xs text-blue-600">⏱️ Presupuesto en 48h</p>
                </div>

                {/* Card 3: Proyectos */}
                <div className="p-5 sm:p-8 rounded-3xl border border-border/30 hover:border-primary/50 transition-all">
                  <div className="text-5xl mb-4">🚀</div>
                  <h3 className="text-xl font-semibold mb-2">Proyecto completo</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Automatización, agente de voz o asesoría
                  </p>
                  <p className="text-xs text-purple-600">⏱️ Reunión en 48h</p>
                </div>
              </div>
            </div>
            
            {/* Contact Info - Only Phone */}
            <div className="space-y-6 pt-8 border-t border-border/30">
              <p className="text-sm text-muted-foreground">
                ¿Es urgente? Llámanos:
              </p>
              <a 
                href="tel:+34916651232"
                className="flex items-center gap-3 text-2xl font-light text-foreground hover:text-primary transition-colors group"
              >
                <Phone className="h-6 w-6 group-hover:scale-110 transition-transform" />
                <span>+34 91 665 12 32</span>
              </a>
              <p className="text-xs text-muted-foreground">
                Lun - Vie: 9:00 - 18:00
              </p>
            </div>
          </div>
          
          {/* Right Column - Clean Form */}
          <div className="space-y-6 animate-fade-in">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Nombre"
                  className="h-14 text-lg"
                />
              </div>
              
              <div className="space-y-2">
                <Input
                  id="empresa"
                  name="empresa"
                  value={formData.empresa}
                  onChange={handleChange}
                  placeholder="Apellido"
                  className="h-14 text-lg"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="tu@email.com"
                className="h-14 text-lg"
              />
            </div>

            <div className="space-y-2">
              <Textarea
                id="mensaje"
                name="mensaje"
                value={formData.mensaje}
                onChange={handleChange}
                required
                rows={6}
                placeholder="Cuéntanos qué necesitas..."
                className="text-lg resize-none"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-16 text-lg font-semibold"
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Enviar mensaje"}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              Te responderemos en menos de 24 horas
            </p>
            </form>
          </div>
          
        </div>
      </section>

      <Footer />
    </div>
  );
}
